import { React, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';

import { useLanguage } from '../../../Context/LanguageContext';
import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';

import useAiTextGenerator from '../AI/useAiTextGenerator';

import { ReactComponent as Ai2Svg } from '../../../image/Ai2.svg';
import { ReactComponent as Check2Svg } from '../../../image/Check2.svg';

export default function AiAssistant(props) {
  const { type, onApply, context, sessionKey } = props;

  const { t, languageMode } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const location = useLocation();

  // 세션 구분 기준 — 페이지 경로 + 타입 + (선택)카테고리 키 //
  const resolvedSessionKey = `${location.pathname}|${type}|${sessionKey ?? ''}`;

  const { status, error, generate } = useAiTextGenerator(resolvedSessionKey);

  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);
  const [isApplied, setIsApplied] = useState(false);
  const [isEmptyWarning, setIsEmptyWarning] = useState(false);

  const isBusy = status === 'init' || status === 'loading';
  const isDenied = status === 'permissionDenied';

  // 입력값 변경 + textarea 높이 자동 조절 //
  function onChangeAiTextInput(e) {
    setInput(e.target.value);
    setIsEmptyWarning(false);

    e.target.style.height = 'auto';
    e.target.style.height = `${e.target.scrollHeight}px`;
  }

  // AI 작성 요청 //
  function onClickAiSend() {
    if (isBusy || isDenied) return;

    if (input.trim() === '') {
      setIsEmptyWarning(true);
      return;
    }

    // 직전 생성 결과 — 있으면 이어서 다듬도록 컨텍스트로 전달 //
    const previousResult = result;

    setIsApplied(false);
    setResult(null);

    (
      async () => {
        const resolvedContext = typeof context === 'function' ? context() : (context || {});

        let contextWithHistory = resolvedContext;

        if (previousResult) {
          contextWithHistory = {
            ...resolvedContext,
            currentTitle: previousResult.title || resolvedContext.currentTitle,
            currentContent: previousResult.content,
          };
        }

        const generated = await generate({ type, userPrompt: input, context: contextWithHistory, language: languageMode });

        if (generated) setResult(generated);
      }
    )();
  }

  // 결과 적용 //
  function onClickApply() {
    if (isApplied || result === null) return;
    if (onApply) onApply(result.title || '', result.content);
    setIsApplied(true);
  }

  // 하단 안내 문구 결정 //
  function decideHintText() {
    if (isDenied) return t('aiAssistant.permissionDenied');
    else if (status === 'rateLimited') return t('aiAssistant.rateLimited', { time: error?.resetAt ? moment(error.resetAt).format('HH:mm') : '' });
    else if (status === 'error') return t('aiAssistant.error');
    else if (isEmptyWarning) return t('aiAssistant.emptyInput');
    else return '';
  }

  // 결과 본문 렌더 — 자동메시지는 {변수}를 주황 칩으로 표시 //
  function renderResultContent(content) {
    if (type !== 'autoMessage') return content;

    return content.split(/(\{[^}]+\})/).map((part, index) => {
      if (/^\{[^}]+\}$/.test(part)) return <VariableChip key={'var-' + index}>{part.slice(1, -1)}</VariableChip>;

      return part;
    });
  }

  const hintText = decideHintText();

  return (
    <AiContentWrapper>
      <ComponentWrapper>
        <AiTitleWrapper>
          <AiIcon $fill={theme.text} />
          <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('aiAssistant.title')}</Text>
        </AiTitleWrapper>
        <AiTextInputWrapper>
          <AiTextInput
            rows={1}
            value={input}
            onChange={onChangeAiTextInput}
            placeholder={t('aiAssistant.placeholder')}
            disabled={isDenied} />
          <AiSendButton $isDisabled={isBusy || isDenied} onClick={onClickAiSend}>
            {
              status === 'loading' ?
                <LoadingSpinner $color={theme.background} />
                :
                <AiIcon $fill={theme.background} />
            }
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.background} cursor='pointer'>{status === 'loading' ? t('aiAssistant.loading') : t('aiAssistant.write')}</Text>
          </AiSendButton>
        </AiTextInputWrapper>
        {
          hintText !== '' &&
          <HintText $isError={status === 'error' || status === 'rateLimited' || isEmptyWarning}>{hintText}</HintText>
        }
      </ComponentWrapper>
      {
        status === 'loading' &&
        <ComponentWrapper>
          <AiResultLoadingBox>
            <SkeletonLine $width='100%' />
            <SkeletonLine $width='92%' />
            <SkeletonLine $width='60%' />
          </AiResultLoadingBox>
        </ComponentWrapper>
      }
      {
        result !== null &&
        <ComponentWrapper>
          <AiResultWrapper>
            {
              result.title !== '' &&
              <AiResultTitle>{result.title}</AiResultTitle>
            }
            <AiResultContent>{renderResultContent(result.content)}</AiResultContent>
            <ApplyAIResultButton onClick={onClickApply}>
              {
                isApplied ?
                  <CheckCircle>
                    <CheckIcon />
                  </CheckCircle>
                  :
                  <UncheckCircle />
              }
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='pointer'>{t('aiAssistant.applyVersion')}</Text>
            </ApplyAIResultButton>
          </AiResultWrapper>
        </ComponentWrapper>
      }
    </AiContentWrapper>
  );
}

const AiContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  gap: 20px;

  /* 전역 드래그 방지(App.css body)의 예외 — 이 컴포넌트 내부는 텍스트 선택/복사 허용 */
  user-select: text;
  -webkit-user-select: text;
  -ms-user-select: text;
`;

const ComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  padding: 0px 0px;

  box-sizing: border-box;
  width: 100%;

  gap: 8px;
`;

const AiTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
  height: 26px;

  gap: 10px;
`;

const AiIcon = styled(Ai2Svg)`
  width: 16px;
  height: 15px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.$fill};
    transition: fill 0.3s ease-in-out;
  }
`;

const AiTextInputWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: flex-start;

  width: 100%;

  gap: 10px;
`;

const AiTextInput = styled.textarea`
  box-sizing: border-box;
  width: 100%;
  min-height: 20px;
  max-height: 150px;

  padding: 10px 15px;

  font-family: NotoSansKR-Medium;
  font-size: 14px;
  color: ${props => props.theme.text};

  border: none;
  border-radius: 8px;
  background-color: ${props => props.theme.chatBoxBackground};

  outline: none;
  resize: none;

  &::placeholder {
    color: ${props => props.theme.subText};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.scrollBar};
    border-radius: 200px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  transition: all 0.3s ease-in-out;
`;

const AiSendButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 5px 0px;

  box-sizing: border-box;
  min-width: 92.8px;
  height: 40px;

  gap: 5px;

  border-radius: 5px;
  background-color: ${props => props.theme.primary};

  opacity: ${props => props.$isDisabled ? `0.5` : `1`};

  @media (hover: hover){
    &:hover {
      cursor: ${props => props.$isDisabled ? `not-allowed` : `pointer`};
    }
  }

  transition: opacity 0.3s ease-in-out;
`;

const LoadingSpinner = styled.div`
  width: 14px;
  height: 14px;

  flex-shrink: 0;

  border: 2px solid ${props => props.$color};
  border-top-color: transparent;
  border-radius: 50%;

  animation: spin 0.7s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const HintText = styled.p`
  margin: 0 0 0 0;

  font-family: NotoSansKR-Regular;
  font-size: 12px;
  color: ${props => props.$isError ? props.theme.pointRed : props.theme.subText};
`;

const AiResultWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  gap: 5px;
`;

const AiResultTitle = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  padding: 5px 15px;

  box-sizing: border-box;
  width: 100%;
  min-height: 40px;

  border-radius: 10px;
  background-color: ${props => props.theme.chatBoxBackground};

  font-family: NotoSansKR-Regular;
  font-size: 14px;
  color: ${props => props.theme.text};
  line-height: 22px;
`;

const AiResultContent = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-height: 40px;

  padding: 15px 15px;

  white-space: pre-wrap;
  word-break: break-word;

  border-radius: 10px;
  background-color: ${props => props.theme.chatBoxBackground};

  font-family: NotoSansKR-Regular;
  font-size: 14px;
  color: ${props => props.theme.text};
  line-height: 22px;
`;

const AiResultLoadingBox = styled.div`
  display: flex;
  flex-direction: column;

  box-sizing: border-box;
  width: 100%;
  min-height: 40px;

  padding: 15px 15px;

  gap: 10px;

  border-radius: 10px;
  background-color: ${props => props.theme.chatBoxBackground};
`;

const SkeletonLine = styled.div`
  width: ${props => props.$width};
  height: 14px;

  border-radius: 4px;

  background: linear-gradient(
    90deg,
    ${props => props.theme.chatBoxBackground} 25%,
    ${props => props.theme.primaryAlpha20} 37%,
    ${props => props.theme.chatBoxBackground} 63%
  );
  background-size: 400% 100%;

  animation: shimmer 1.4s ease-in-out infinite;

  @keyframes shimmer {
    0% { background-position: 100% 0; }
    100% { background-position: 0 0; }
  }
`;

const VariableChip = styled.span`
  display: inline-flex;
  align-items: center;

  height: 22px;
  padding: 0px 8px;
  margin: 0px 2px;

  border-radius: 5px;
  background-color: ${props => props.theme.primaryAlpha20};

  font-family: NotoSansKR-Regular;
  font-size: 14px;
  color: ${props => props.theme.text};

  vertical-align: middle;
`;

const ApplyAIResultButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  box-sizing: border-box;
  width: 100%;
  height: 40px;

  gap: 10px;

  border-radius: 200px;
  border: 1px solid transparent;
  background-color: ${props => props.theme.primaryAlpha20};

  color: ${props => props.theme.text};

  @media (hover: hover){
    &:hover {
      cursor: pointer;
      border: 1px solid ${props => props.theme.primary};
    }
  }

  transition: all 0.3s ease-in-out;
`;

const CheckCircle = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 18px;
  height: 18px;

  border-radius: 18px;
  background-color: ${props => props.theme.primary};
`;

const UncheckCircle = styled.div`
  box-sizing: border-box;

  width: 18px;
  height: 18px;

  border-radius: 18px;
  background-color: transparent;

  background-image: ${props => `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 18 18'><circle cx='9' cy='9' r='8.5' fill='none' stroke='${encodeURIComponent(props.theme.primary)}' stroke-width='1' stroke-dasharray='3 4' stroke-linecap='round'/></svg>")`};
  background-size: 100% 100%;
  background-repeat: no-repeat;
`;

const CheckIcon = styled(Check2Svg)`
  width: 11px;
  height: 10px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.subBackground};
    transition: fill 0.3s ease-in-out;
  }
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  @media (hover: hover) {
    &:hover {
      cursor: ${props => props.cursor};
    }
  }
`;
